import {getScrollableParent} from '@github-ui/get-scrollable-parent'
import {getBaseStickyHeaderHeight, observeStickyHeaderHeight, unobserveStickyHeaderHeight} from '@github-ui/sticky-css'
import {type RefObject, useCallback, useEffect, useRef, useState, useSyncExternalStore} from 'react'

import {resolveCommentJumpFocusTarget} from '../components/timeline/timeline-focus-target'
import {VALUES} from '../constants/values'
import {useHasScrolledForHighlight} from './use-has-scrolled-for-highlight'

function subscribeToStickyHeaderHeight(notify: () => void) {
  observeStickyHeaderHeight(notify)
  return () => unobserveStickyHeaderHeight(notify)
}

function isCurrentRouterHash(hash: string) {
  return hash.replace(/^#/, '') === window.location.hash.slice(1)
}

type ScrollToHighlightedOptions = {
  commentJumpActivationId?: number | string
  failureFocusTargetRef?: RefObject<HTMLElement | null>
  centerCommentJump?: boolean
  routeHash?: string
  onFailureFocusSkipped?: (target: HTMLElement) => void
}

export const useNewScrollToHighlighted = (
  isHighlightLoaded: boolean,
  highlightedItemRef: RefObject<HTMLLIElement | null>,
  highlightedEventId: string | undefined,
  highlightedReadyToScroll: boolean,
  {
    commentJumpActivationId,
    failureFocusTargetRef,
    centerCommentJump = false,
    routeHash,
    onFailureFocusSkipped,
  }: ScrollToHighlightedOptions = {},
) => {
  const [lastScrolledItemId, setLastScrolledItemId] = useState<string | undefined>(undefined)
  // `hasScrolled` resets when the highlight target changes (and the new target is
  // loaded), so a user navigating between highlights triggers a fresh scroll.
  const [hasScrolled, setHasScrolled] = useHasScrolledForHighlight(
    isHighlightLoaded,
    highlightedEventId,
    lastScrolledItemId,
  )

  const notificationShelfHeight = useSyncExternalStore(
    subscribeToStickyHeaderHeight,
    getBaseStickyHeaderHeight,
    getBaseStickyHeaderHeight,
  )
  const previousNotificationShelfHeightRef = useRef(notificationShelfHeight)
  const jumpFocusCancelledRef = useRef(false)
  const focusedActivationRef = useRef<number | string | undefined>(undefined)
  const failureFeedbackHandledRef = useRef(false)
  const scrollGenerationRef = useRef(0)
  const anchorScrollMarginRef = useRef<{anchor: HTMLElement; originalMargin: string} | undefined>(undefined)

  useEffect(() => {
    return () => {
      const margin = anchorScrollMarginRef.current
      if (!margin) return
      if (margin.originalMargin) {
        margin.anchor.style.setProperty('--issue-comment-scroll-margin-top', margin.originalMargin)
      } else {
        margin.anchor.style.removeProperty('--issue-comment-scroll-margin-top')
      }
      anchorScrollMarginRef.current = undefined
    }
  }, [commentJumpActivationId, highlightedEventId])

  useEffect(() => {
    scrollGenerationRef.current += 1
    jumpFocusCancelledRef.current = false
    if (commentJumpActivationId === undefined) {
      focusedActivationRef.current = undefined
      return
    }

    const cancel = () => {
      jumpFocusCancelledRef.current = true
    }
    document.addEventListener('pointerdown', cancel)
    document.addEventListener('keydown', cancel)
    document.addEventListener('wheel', cancel, {passive: true})
    return () => {
      scrollGenerationRef.current += 1
      document.removeEventListener('pointerdown', cancel)
      document.removeEventListener('keydown', cancel)
      document.removeEventListener('wheel', cancel)
    }
  }, [commentJumpActivationId, highlightedEventId])

  useEffect(() => {
    if (commentJumpActivationId !== undefined) setHasScrolled(false)
  }, [commentJumpActivationId, setHasScrolled])

  useEffect(() => {
    failureFeedbackHandledRef.current = false
  }, [commentJumpActivationId, highlightedEventId, failureFocusTargetRef])

  useEffect(() => {
    if (commentJumpActivationId === undefined) return
    if (centerCommentJump && routeHash !== undefined && !isCurrentRouterHash(routeHash)) return
    if (isHighlightLoaded && (!highlightedReadyToScroll || focusedActivationRef.current === commentJumpActivationId))
      return

    const targetRef = isHighlightLoaded ? highlightedItemRef : failureFocusTargetRef
    const target = targetRef?.current
    if (!target) return
    const frame = requestAnimationFrame(() => {
      if (!target.isConnected || targetRef.current !== target) return
      if (centerCommentJump && routeHash !== undefined && !isCurrentRouterHash(routeHash)) return
      if (!isHighlightLoaded && failureFeedbackHandledRef.current) return
      if (jumpFocusCancelledRef.current) {
        if (!isHighlightLoaded) {
          failureFeedbackHandledRef.current = true
          onFailureFocusSkipped?.(target)
        }
        return
      }

      const destination = isHighlightLoaded ? resolveCommentJumpFocusTarget(target) : target
      destination.focus({preventScroll: true})
      if (isHighlightLoaded) focusedActivationRef.current = commentJumpActivationId
      else {
        failureFeedbackHandledRef.current = true
        destination.scrollIntoView({behavior: 'instant', block: 'center', inline: 'nearest'})
      }
    })
    return () => cancelAnimationFrame(frame)
  }, [
    centerCommentJump,
    routeHash,
    commentJumpActivationId,
    isHighlightLoaded,
    highlightedReadyToScroll,
    highlightedItemRef,
    failureFocusTargetRef,
    onFailureFocusSkipped,
  ])

  const scrollToHighlightedEvent = useCallback(
    async (ref: RefObject<HTMLLIElement | null>) => {
      if (!ref.current || !highlightedReadyToScroll) return
      // Native hash changes reach the viewer before the router commits its scroll restoration.
      if (centerCommentJump && routeHash !== undefined && !isCurrentRouterHash(routeHash)) return

      const generation = scrollGenerationRef.current
      await mediaLoaded()
      if (generation !== scrollGenerationRef.current) return
      if (commentJumpActivationId !== undefined && jumpFocusCancelledRef.current) return

      if (centerCommentJump && commentJumpActivationId !== undefined && ref.current) {
        const destination = resolveCommentJumpFocusTarget(ref.current)
        const anchor = ref.current.querySelector<HTMLElement>('[id^="issuecomment-"]')
        if (anchor) {
          const scrollport = getScrollableParent(destination) ?? document.documentElement
          const destinationBounds = destination.getBoundingClientRect()
          const anchorBounds = anchor.getBoundingClientRect()
          const margin =
            scrollport.clientHeight / 2 - destinationBounds.height / 2 - (destinationBounds.top - anchorBounds.top)
          anchorScrollMarginRef.current ??= {
            anchor,
            originalMargin: anchor.style.getPropertyValue('--issue-comment-scroll-margin-top'),
          }
          // Later native/router hash scrolls must use the same alignment as this jump.
          anchor.style.setProperty('--issue-comment-scroll-margin-top', `${Math.max(0, margin)}px`)
        }
        // Center the heading rather than a tall comment's body, including in nested scrollports.
        destination.scrollIntoView({
          behavior: 'instant',
          block: 'center',
          inline: 'nearest',
        })
      } else {
        ref.current?.scrollIntoView({behavior: 'instant', block: 'start', inline: 'start'})
        window.scrollBy({top: -(VALUES.stickyHeaderHeight + notificationShelfHeight), behavior: 'instant'})
      }
      setLastScrolledItemId(highlightedEventId)
      setHasScrolled(true)
    },
    [
      centerCommentJump,
      commentJumpActivationId,
      highlightedEventId,
      highlightedReadyToScroll,
      notificationShelfHeight,
      routeHash,
      setHasScrolled,
    ],
  )

  // The shelf can arrive after the highlighted item has already scrolled. Its shared height store
  // notifies us on insertion/removal, so rerun the scroll with the current offset.
  useEffect(() => {
    if (previousNotificationShelfHeightRef.current !== notificationShelfHeight) {
      previousNotificationShelfHeightRef.current = notificationShelfHeight
      setHasScrolled(false)
    }
  }, [notificationShelfHeight, setHasScrolled])

  // Scroll to the highlighted item if it's loaded.
  useEffect(() => {
    if (!isHighlightLoaded || !highlightedItemRef?.current || hasScrolled) return

    scrollToHighlightedEvent(highlightedItemRef)
  }, [hasScrolled, highlightedItemRef, isHighlightLoaded, scrollToHighlightedEvent])
}

// Copied from: app/assets/modules/github/behaviors/timeline/progressive.ts.
// resolves when comment body videos have loaded enough data to render the preview image
async function videosReady(): Promise<unknown> {
  const videos: NodeListOf<HTMLVideoElement> = document.querySelectorAll(VALUES.commentVideo)
  const videoLoads = Array.from(videos).map(v => {
    return new Promise<HTMLVideoElement>(resolve => {
      if (v.readyState >= v.HAVE_METADATA) {
        resolve(v)
      } else {
        // don't wait forever :)
        const timeout = setTimeout(() => resolve(v), VALUES.scrollWaitMediaTimeout)
        const done = () => {
          clearTimeout(timeout)
          resolve(v)
        }
        v.addEventListener('loadeddata', () => {
          if (v.readyState >= v.HAVE_METADATA) done()
        })
        v.addEventListener('error', () => done())
      }
    })
  })
  return Promise.all(videoLoads)
}

// Copied from: app/assets/modules/github/behaviors/timeline/progressive.ts.
// resolves when comment body images are loaded
async function imagesReady(): Promise<unknown> {
  const images: NodeListOf<HTMLImageElement> = document.querySelectorAll(VALUES.commentImage)
  const imageLoads = Array.from(images).map(i => {
    new Promise<HTMLImageElement>(resolve => {
      if (i.complete) {
        resolve(i)
      } else {
        const timeout = setTimeout(() => resolve(i), VALUES.scrollWaitMediaTimeout)
        const done = () => {
          clearTimeout(timeout)
          resolve(i)
        }
        i.addEventListener('load', () => done())
        i.addEventListener('error', () => done())
      }
    })
  })
  return Promise.all(imageLoads)
}

// Copied from: app/assets/modules/github/behaviors/timeline/progressive.ts.
async function mediaLoaded(): Promise<unknown> {
  return Promise.all([videosReady(), imagesReady()])
}
